import { Component, model, input, effect, inject, ChangeDetectorRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BlockScheduleFileModel } from '../../../../models/project.model';
import { FileControlValue, LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';
import { parseValidationConfig, FileValidationConfig } from '../../../../models/validation-config.model';
import { downloadChecklistFileResponse } from '../../../../utils/checklist-file-download.utils';
import { ProjecttemplateService } from '../../../../services/projecttemplate.service';


@Component({
  selector: 'app-attachment-control',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="checklist-control-container">
      <div class="ctrl-container-hdr">
        <div class="att-label-group">
          <label class="checklist-control-label">
            {{ lineItem().name }}
            @if (lineItem().isRequired) {
              <span class="required-indicator">*</span>
            }
          </label>
          @if (countHint()) {
            <span class="att-count" [class.att-count--limit]="atLimit()">{{ countHint() }}</span>
          } @else if (newFiles.length + existingFiles.length > 0) {
            <span class="att-count">({{ newFiles.length + existingFiles.length }} Files)</span>
          }
        </div>
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>

      <input
        #fileInput
        type="file"
        (change)="onFileSelect($event)"
        style="display: none"
        [disabled]="disabled()"
        multiple
      />

      <button type="button" class="ctrl-photo-add" (click)="fileInput.click()"
        [disabled]="disabled() || atLimit()"
        [title]="atLimit() ? 'Maximum ' + maxCount() + ' files allowed' : ''">
        <i class="ri-upload-2-line"></i>Choose Files
      </button>

      <!-- New Files (Not Yet Uploaded) -->
      @if (newFiles.length > 0) {
        <div class="att-file-list">
          <div class="att-file-list-label">Selected</div>
          @for (file of newFiles; track $index) {
            <div class="att-file-item">
              <div class="att-file-info">
                <i [class]="getFileIcon(file.name) + ' att-file-icon'"></i>
                <div class="att-file-meta">
                  <span class="att-file-name">{{ file.name }}</span>
                  <span class="att-file-size">{{ formatFileSize(file.size) }}</span>
                </div>
              </div>
              @if (!disabled()) {
                <button type="button" class="ctrl-icon-btn ctrl-icon-btn--danger" (click)="removeNewFile($index)" title="Remove">
                  <i class="ri-close-line"></i>
                </button>
              }
            </div>
          }
        </div>
      }

      <!-- Existing Uploaded Files -->
      @if (existingFiles.length > 0) {
        <div class="att-file-list">
          <div class="att-file-list-label">Uploaded</div>
          @for (file of existingFiles; track file.id) {
            <div class="att-file-item">
              <div class="att-file-info">
                <i [class]="getFileIcon(file.fileName, file.fileType) + ' att-file-icon att-file-icon--ok'"></i>
                <div class="att-file-meta">
                  <span class="att-file-name">{{ file.fileName }}</span>
                </div>
              </div>
              <div class="att-file-actions">
                <button type="button" class="ctrl-icon-btn" (click)="downloadFile(file)" title="Download">
                  <i class="ri-download-2-line"></i>
                </button>
                @if (!disabled()) {
                  <button type="button" class="ctrl-icon-btn ctrl-icon-btn--danger" (click)="deleteFile(file)" title="Delete">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }

      @if (newFiles.length === 0 && existingFiles.length === 0) {
        <div class="att-empty">No files selected</div>
      }
      @if (errors().length > 0) {
        @for (err of errors(); track $index) {
          <small class="p-error d-block">{{ err }}</small>
        }
      } @else if (hint()) {
        <small class="text-color-secondary">{{ hint() }}</small>
      }
    </div>
  `,
  styles: [`
    .checklist-control-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 14px 16px;
      background: var(--color-white);
    }
    .att-label-group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .att-count {
      font-size: 12px;
      color: var(--color-grey-500);
    }
    .att-count--limit {
      color: #EB4747;
      font-weight: 600;
    }
    .att-file-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .att-file-list-label {
      font-size: 12px;
      font-weight: var(--font-weight-semibold);
      color: var(--color-grey-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .att-file-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 12px;
      border: 1px solid var(--color-border-default);
      border-radius: 6px;
      background: var(--color-white);
    }
    .att-file-info {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .att-file-icon {
      font-size: 20px;
      color: var(--color-primary-500);
      flex-shrink: 0;
    }
    .att-file-icon--ok {
      color: var(--color-success-mid);
    }
    .att-file-meta {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .att-file-name {
      font-size: 14px;
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .att-file-size {
      font-size: 12px;
      color: var(--color-grey-500);
    }
    .att-file-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }
    .att-empty {
      font-size: 14px;
      color: var(--color-grey-500);
    }
  `]
})
export class AttachmentControlComponent {
  lineItem = input.required<LineItemModel>();
  value = model<FileControlValue>({ newFiles: [], existingFileIds: [] });
  disabled = input<boolean>(false);
  showError = input<boolean>(false);
  existingFilesData = input<BlockScheduleFileModel[]>([]);

  private validationService = inject(ValidationService);

  /** Configured max file count — Infinity when not set */
  maxCount = computed(() => {
    const cfg = parseValidationConfig<FileValidationConfig>(this.lineItem().validationConfig);
    return cfg?.maxCount ?? Infinity;
  });

  /** Total files currently held (new + existing) */
  totalCount = computed(() =>
    (this.value()?.newFiles?.length ?? 0) + (this.value()?.existingFileIds?.length ?? 0)
  );

  /** True when the user has reached or exceeded the configured limit */
  atLimit = computed(() => isFinite(this.maxCount()) && this.totalCount() >= this.maxCount());

  /** Remaining slots before the limit is hit */
  remainingSlots = computed(() =>
    isFinite(this.maxCount()) ? Math.max(0, this.maxCount() - this.totalCount()) : Infinity
  );

  countHint = computed(() => {
    if (!isFinite(this.maxCount())) return '';
    if (this.atLimit()) return `${this.totalCount()}/${this.maxCount()} files — limit reached`;
    return `${this.totalCount()}/${this.maxCount()} files`;
  });

  errors = computed(() => {
    if (!this.showError()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hint = computed(() => this.errors().length > 0 ? '' : this.validationService.getHint(this.lineItem()));

  newFiles: File[] = [];
  existingFiles: BlockScheduleFileModel[] = [];

  constructor(private projecttemplateService: ProjecttemplateService, private cdr: ChangeDetectorRef) {
    // Sync value model to local arrays
    effect(() => {
      const val = this.value();
      if (val) {
        this.newFiles = val.newFiles || [];
      }
    });

    // Sync existing files from input
    effect(() => {
      this.existingFiles = this.existingFilesData() || [];
    });
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const currentValue = this.value() || { newFiles: [], existingFileIds: [] };
      const slots = this.remainingSlots();
      const newFilesList = Array.from(input.files).slice(0, isFinite(slots) ? slots : undefined);

      if (newFilesList.length > 0) {
        this.value.set({
          newFiles: [...(currentValue.newFiles || []), ...newFilesList],
          existingFileIds: currentValue.existingFileIds || []
        });
      }

      input.value = '';
    }
  }

  removeNewFile(index: number): void {
    const currentValue = this.value() || { newFiles: [], existingFileIds: [] };
    const updatedNewFiles = [...(currentValue.newFiles || [])];
    updatedNewFiles.splice(index, 1);

    this.value.set({
      newFiles: updatedNewFiles,
      existingFileIds: currentValue.existingFileIds || []
    });
  }

  deleteFile(file: BlockScheduleFileModel): void {
    // Optimistically remove from UI
    this.existingFiles = this.existingFiles.filter(f => f.id !== file.id);

    const currentValue = this.value() || { newFiles: [], existingFileIds: [] };
    this.value.set({
      newFiles: currentValue.newFiles || [],
      existingFileIds: (currentValue.existingFileIds || []).filter(id => id !== file.id)
    });

    this.cdr.markForCheck();

    // Delete from backend
    this.projecttemplateService.deleteFile(file.id).subscribe({
      error: () => {
        // Restore file on failure
        this.existingFiles = [...this.existingFiles, file];
        const restoredValue = this.value() || { newFiles: [], existingFileIds: [] };
        this.value.set({
          newFiles: restoredValue.newFiles || [],
          existingFileIds: [...(restoredValue.existingFileIds || []), file.id]
        });
        this.cdr.markForCheck();
      }
    });
  }

  downloadFile(file: BlockScheduleFileModel): void {
    this.projecttemplateService.downloadChecklistAttachment(file.id).subscribe({
      next: response => {
        downloadChecklistFileResponse(response, file.fileName || 'download');
      },
      error: error => console.error('Error downloading checklist attachment:', error)
    });
  }


  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getFileIcon(fileName?: string | null, fileType?: string | null): string {
    const ext = this.getFileExtension(fileName, fileType);

    if (['jpg', 'jpeg', 'jfif', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif', 'tif', 'tiff'].includes(ext)) {
      return 'ri-image-line';
    }
    if (ext === 'pdf') return 'ri-file-pdf-2-line';
    if (['doc', 'docx', 'rtf'].includes(ext)) return 'ri-file-word-2-line';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'ri-file-excel-2-line';
    if (['ppt', 'pptx'].includes(ext)) return 'ri-file-ppt-2-line';
    if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) return 'ri-file-zip-line';
    if (['txt', 'md', 'log'].includes(ext)) return 'ri-file-text-line';
    if (['json', 'xml', 'html', 'css', 'js', 'ts', 'sql'].includes(ext)) return 'ri-file-code-line';
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'ri-video-line';
    if (['mp3', 'wav', 'm4a', 'aac', 'flac'].includes(ext)) return 'ri-music-2-line';

    return 'ri-file-line';
  }

  private getFileExtension(fileName?: string | null, fileType?: string | null): string {
    const normalizedName = (fileName || '').trim().toLowerCase();
    const normalizedType = (fileType || '').trim().toLowerCase();
    const source = normalizedName || normalizedType;
    const dotIndex = source.lastIndexOf('.');

    if (dotIndex >= 0 && dotIndex < source.length - 1) {
      return source.slice(dotIndex + 1);
    }

    return normalizedType.replace(/^\./, '');
  }

  private speechService = inject(SpeechService);

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
